/* eslint-disable react-refresh/only-export-components */
import React, { useContext, useState, useCallback, ReactNode } from "react";
import { createSharedContext } from "@/lib/utils";

export interface BreadcrumbItem {
  label: string;
  path: string;
}

export interface PageAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary" | "outline";
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
}

export interface TimeRangeOption {
  id: string;
  label: string;
  value: string;
}

export interface PageMetric {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  status?: "default" | "success" | "warning" | "danger" | "info";
  tooltip?: string;
}

export interface PageHeaderConfig {
  title?: string;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: PageAction[];
  timeRange?: {
    value: string;
    options: TimeRangeOption[];
    onChange: (value: string) => void;
  };
  toolbar?: ReactNode;
  showHeader?: boolean;
  fullBleed?: boolean;
  lastUpdated?: Date;
  metrics?: PageMetric[];
}

interface PageContextValue {
  config: PageHeaderConfig;
  setConfig: (config: Partial<PageHeaderConfig>) => void;
  updateConfig: (updates: Partial<PageHeaderConfig>) => void;
  resetConfig: () => void;
}

// =============================================================================
// CONTEXT
// =============================================================================

export const PageContext = createSharedContext<PageContextValue | undefined>(
  "__9s_page_react_context__",
  undefined,
);

// =============================================================================
// PROVIDER
// =============================================================================

export const PageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfigState] = useState<PageHeaderConfig>({
    showHeader: true,
    metrics: [],
  });

  const setConfig = useCallback((newConfig: Partial<PageHeaderConfig>) => {
    setConfigState((prev) => ({
      ...prev,
      ...newConfig,
    }));
  }, []);

  const updateConfig = useCallback((updates: Partial<PageHeaderConfig>) => {
    setConfigState((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetConfig = useCallback(() => {
    setConfigState({ showHeader: true, metrics: [] });
  }, []);

  return (
    <PageContext.Provider value={{ config, setConfig, updateConfig, resetConfig }}>{children}</PageContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const usePageHeader = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error("usePageHeader must be used within PageProvider");
  }
  return context;
};
